import { Struct } from 'contexts/structs/types/types'
import { adsSelectors } from 'ducks/ads'
import useAdStructs, { BannerConfig } from 'elements/ads/useAdStructs'
import { AdIframe, AdTextLink, BannerFilters, filterAvailableAds } from 'elements/BannerAd'
import { Box, styled } from 'opensolar-ui'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'util/featureFlags'

const Container = styled('div')(() => ({
  width: '100%',
  position: 'relative',
  margin: '0 auto',
  overflow: 'hidden',
}))

const ScrollContainer = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  width: '100%',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollSnapType: 'x mandatory',
}))

const ChildContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  scrollSnapAlign: 'start',
  flexShrink: 0,
  flexBasis: '100%',
  minWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
}))

interface IndicatorButtonProps {
  active?: boolean
}

const IndicatorButton = styled(Box)<IndicatorButtonProps>(({ theme, active }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: active ? '#4272DD' : theme.palette.grey[300],
  transition: theme.transitions.create(['background-color']),
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: active ? '#4272DD' : theme.palette.grey[400],
  },
}))

const IndicatorRail = styled(Box)(() => ({
  position: 'absolute',
  left: '50%',
  bottom: '22px',
  transform: 'translateX(-50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  zIndex: 1,
}))

interface CarouselContainerProps {
  children: React.ReactNode
  autoScrollInterval?: number
  pauseOnHover?: boolean
  showIndicators?: boolean
}

const CarouselContainer = ({
  children,
  autoScrollInterval = 5000,
  pauseOnHover = true,
  showIndicators = true,
}: CarouselContainerProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const carouselContainerRef = useRef<HTMLDivElement>(null)
  const childrenArray = React.Children.toArray(children)
  const totalPages = childrenArray.length

  const scrollToPage = useCallback(
    (pageIndex: number) => {
      const container = carouselContainerRef.current
      if (!container) return

      const normalizedIndex = pageIndex >= totalPages ? 0 : pageIndex
      const scrollPosition = normalizedIndex * container.clientWidth

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      })

      setCurrentPage(normalizedIndex)
    },
    [totalPages]
  )

  useEffect(() => {
    if (!autoScrollInterval || isPaused || totalPages <= 1) return

    const intervalId = setInterval(() => {
      scrollToPage(currentPage + 1)
    }, autoScrollInterval)

    return () => clearInterval(intervalId)
  }, [currentPage, isPaused, scrollToPage, autoScrollInterval, totalPages])

  const handleMouseEnter = () => {
    if (pauseOnHover) setIsPaused(true)
  }

  const handleMouseLeave = () => {
    if (pauseOnHover) setIsPaused(false)
  }

  return (
    <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <ScrollContainer ref={carouselContainerRef}>
        {React.Children.map(children, (child) => (
          <ChildContainer>{child}</ChildContainer>
        ))}
      </ScrollContainer>
      {showIndicators && totalPages > 1 && (
        <IndicatorRail>
          {Array.from({ length: totalPages }).map((_, index) => (
            <IndicatorButton
              key={index}
              active={currentPage === index}
              onClick={() => scrollToPage(index)}
              component="button"
            />
          ))}
        </IndicatorRail>
      )}
    </Container>
  )
}

type CarouselAdBannerProps = {
  formats: string[]
  placement: string
  maxSlides?: number
  containerStyle?: React.CSSProperties
  autoScrollInterval?: number
  showIndicators?: boolean
}

const CarouselAdBanner: React.FunctionComponent<CarouselAdBannerProps> = ({
  formats,
  placement,
  maxSlides = 10,
  containerStyle,
  autoScrollInterval = 5000,
  showIndicators = true,
}) => {
  const enableAdsFeatureFlag = useFeatureFlag('ads', 'on')
  const enableAdsForOrg = useSelector(adsSelectors.getEnableAdsForOrg)
  const enableAds = enableAdsFeatureFlag && enableAdsForOrg
  const promoConfigs = useAdStructs<BannerConfig>('banner-ad')

  if (!enableAds) return null

  const adFilters: BannerFilters = { formats, placement }
  const availableAds = filterAvailableAds(promoConfigs, adFilters).filter(
    (struct) => struct.data.iframeUrl || struct.data.textLinkLabel
  )

  if (!availableAds.length) return null

  const slides = availableAds.slice(0, maxSlides)

  return (
    <div style={containerStyle}>
      <CarouselContainer pauseOnHover autoScrollInterval={autoScrollInterval} showIndicators={showIndicators}>
        {slides.map((struct: Struct<BannerConfig>) => {
          const bannerConfig = struct.data
          if (bannerConfig.iframeUrl) {
            return <AdIframe key={struct.key} bannerConfig={bannerConfig} placement={placement} rounded={false} />
          }
          if (bannerConfig.textLinkLabel) {
            return <AdTextLink key={struct.key} bannerConfig={bannerConfig} placement={placement} />
          }
          return null
        })}
      </CarouselContainer>
    </div>
  )
}

export default CarouselAdBanner
